import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ThumbsUp, ThumbsDown, RotateCw, Check } from 'lucide-react';
import { sendBiqResponseFeedback } from '../../utils/api';

const iconButtonClass =
  'p-1.5 rounded-md text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2] hover:bg-black/5 dark:hover:bg-white/10 transition-colors disabled:opacity-40 disabled:pointer-events-none';


const activePositiveClass =
  '!text-bloom-green hover:!text-bloom-green dark:hover:!text-bloom-green disabled:!opacity-100';
const activeNegativeClass =
  '!text-red-500 hover:!text-red-500 dark:hover:!text-red-500 disabled:!opacity-100';

// Hidden but still laid out, so revealing the actions never shifts the answer
// below them. Applied per button rather than to the row: opacity on the row
// would clamp its children, and the recorded thumb has to stay visible inside
// an otherwise hidden row. Keyboard users get the same reveal via focus-within.
// `invisible` carries the hiding, not `opacity-0` alone: these buttons also
// use `disabled:opacity-40`, which lands later in the stylesheet at equal
// specificity and would otherwise leave a disabled button ghosted at 40%.
// Visibility outranks any opacity, and on hover the disabled fade still reads.
const revealOnHoverClass =
  'invisible opacity-0 pointer-events-none transition-opacity duration-150 '
  + 'group-hover/msg:visible group-hover/msg:opacity-100 group-hover/msg:pointer-events-auto '
  + 'group-focus-within/msg:visible group-focus-within/msg:opacity-100 '
  + 'group-focus-within/msg:pointer-events-auto';

/**
 * Copy, thumbs up/down, and regenerate controls below a BiQ assistant message.
 * All actions POST /feedback (copy, feedback, regenerate).
 *
 * `revealOnHover` keeps the buttons hidden until the message is hovered
 * (desktop). Touch devices pass false and keep them static -- there is nothing
 * to hover. The one exception is a thumb the user already picked: it stays
 * visible so the feedback they left still reads as recorded. The rest of the
 * row hides with it, so a rated message is no noisier than an unrated one.
 */
const BiqResponseActions = ({
  chatId,
  content,
  userFeedback = null,
  onUserFeedbackChange,
  onRegenerate,
  isStreaming = false,
  revealOnHover = false,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState(null);

  if (isStreaming) return null;

  const hideUnlessHovered = revealOnHover ? revealOnHoverClass : '';

  const recordFeedback = async (action, feedbackValue) => {
    if (!chatId) return;
    setPending(action);
    try {
      await sendBiqResponseFeedback(chatId, action, feedbackValue);
    } catch (e) {
      console.error('BiQ feedback failed:', e);
    } finally {
      setPending(null);
    }
  };

  const handleCopy = async () => {
    const text = (content || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      void recordFeedback('copy');
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const handleThumbs = async (value) => {
    if (userFeedback === value) return;
    onUserFeedbackChange?.(value);
    await recordFeedback('feedback', value);
  };

  const handleRegenerate = () => {
    if (isStreaming) return;
    // Fire-and-forget: tracking must never delay the regeneration itself
    // (the feedback endpoint can be slow while BigQuery catches up).
    if (chatId) {
      sendBiqResponseFeedback(chatId, 'regenerate').catch((e) => {
        console.error('BiQ feedback failed:', e);
      });
    }
    onRegenerate?.();
  };

  return (
    <div className="flex items-center gap-0.5 mt-2 -ml-1.5">
      <button
        type="button"
        onClick={handleCopy}
        disabled={!content?.trim() || pending === 'copy'}
        className={`${iconButtonClass} ${hideUnlessHovered}`}
        aria-label={t('biq.responseActions.copy')}
        title={t('biq.responseActions.copy')}
      >
        {copied ? <Check size={16} className="text-bloom-green" /> : <Copy size={16} />}
      </button>

      <button
        type="button"
        onClick={() => handleThumbs('positive')}
        disabled={!chatId || pending === 'feedback'}
        className={`${iconButtonClass} ${userFeedback === 'positive' ? activePositiveClass : hideUnlessHovered}`}
        aria-label={t('biq.responseActions.thumbsUp')}
        title={t('biq.responseActions.thumbsUp')}
      >
        <ThumbsUp
          size={16}
          fill={userFeedback === 'positive' ? 'currentColor' : 'none'}
          className={userFeedback === 'positive' ? 'text-bloom-green' : undefined}
        />
      </button>

      <button
        type="button"
        onClick={() => handleThumbs('negative')}
        disabled={!chatId || pending === 'feedback'}
        className={`${iconButtonClass} ${userFeedback === 'negative' ? activeNegativeClass : hideUnlessHovered}`}
        aria-label={t('biq.responseActions.thumbsDown')}
        title={t('biq.responseActions.thumbsDown')}
      >
        <ThumbsDown
          size={16}
          fill={userFeedback === 'negative' ? 'currentColor' : 'none'}
          className={userFeedback === 'negative' ? 'text-red-500' : undefined}
        />
      </button>

      <button
        type="button"
        onClick={handleRegenerate}
        disabled={isStreaming || !onRegenerate}
        className={`${iconButtonClass} ${hideUnlessHovered}`}
        aria-label={t('biq.responseActions.regenerate')}
        title={t('biq.responseActions.regenerate')}
      >
        <RotateCw size={16} />
      </button>
    </div>
  );
};

export default BiqResponseActions;
